import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "antd";
import Button from "../button";
import Icon from "../../assets/icons";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import Input from "../input";
import useApi from "../hooks/request";
import { Controller } from "react-hook-form";

interface GRNReceiptProps {
    isVisible: boolean;
    onClose: () => void;
    purchase: any;
}

interface Item {
    id: string;
    [key: string]: any;
}

interface TransformedItem {
    _id: string;
    // name: string;
    // quantity: number;
    quantityReceived: number;
    quantityAccepted: number;
}

interface TransformedData {
    receiptDate: string;
    Items: TransformedItem[];
}

const GRNReceipt: React.FC<GRNReceiptProps> = ({
    isVisible,
    onClose,
    purchase
}) => {
    
    const [items, setItems] = useState(purchase?.Items || []);

    useEffect(() => {
        if (purchase?.Items) {
            setItems(purchase.Items);
        }
    }, [purchase]);


    const updateQuantity = (id: string, field: string, value: any): void => {
        setItems(items?.map((item: Item) => 
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const {
        handleSubmit,
        trigger,
        control,
        reset,
        watch,
        formState: { errors },  
    } = useForm({
        mode: "onChange",
    });

    // Watch all form values to enable cross-field validation
    const watchedValues = watch();

    const { loading, makeRequest } = useApi(`/purchase-order/${purchase?._id}/receipt`, "POST");

    const handleCreateReceipt = handleSubmit(async (formData) => {
        // Transform the form data to match your API expectations
      

        const transformedData: TransformedData = {
            receiptDate: purchase?.purchaseRequisition?.dateRequired,
            Items: items.map((item: Item, index: number): TransformedItem => ({
                _id: item?._id?._id,
                // quantity: item.quantity,
                quantityReceived: formData[`quantityReceived_${index}`] as number,
                quantityAccepted: formData[`quantityAccepted_${index}`] as number
            }))
        };

        const [response] = await makeRequest(transformedData);

        if (response.status) {
            showToast(response.message || "Receipt created successfully", true, {
                position: "top-right",
            });
            onClose();
            reset();
            window.location.reload();
        } else {
            showToast(response?.message || "Failed to create receipt", false, {
                position: "top-right",
            });
        }
    });

    const renderStepContent = () => {
        return (
            <>
                <Controller
                    name="receiptDate"
                    control={control}
                    defaultValue={purchase?.purchaseRequisition?.dateRequired ? 
                        new Date(purchase.purchaseRequisition.dateRequired).toLocaleDateString("en-US") : 
                        ""
                    }
                    rules={{
                        required: "Receipt date is required",
                    }}
                    render={({ field, fieldState }) => (
                        <Input 
                            {...field}
                            value={field.value || (purchase?.purchaseRequisition?.dateRequired ? 
                                new Date(purchase.purchaseRequisition.dateRequired).toLocaleDateString("en-US") : 
                                ""
                            )}
                            className="w-full"
                            placeholder="Enter Receipt Date"
                            error={fieldState?.error?.message}
                            onChange={field.onChange}
                            readOnly={true}
                            autoComplete="off"
                            label="Receipt Date"
                        />
                    )}
                />

                <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="text-left p-4 font-medium text-gray-700 border-b">Item</th>
                                <th className="text-center p-4 font-medium text-gray-700 border-b">Quantity Ordered</th>
                                <th className="text-center p-4 font-medium text-gray-700 border-b">Quantity Received</th>
                                <th className="text-center p-4 font-medium text-gray-700 border-b">Quantity Accepted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item: Item, index: number) => (
                                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="p-4 border-b text-gray-800">{item.name}</td>
                                    <td className="p-4 border-b text-center">
                                        <Input 
                                            value={item.quantity}
                                            className="w-auto px-5 py-2 rounded-full text-center"
                                            readOnly={true}
                                            type="number"
                                        />
                                    </td>
                                    <td className="p-4 border-b text-center">
                                        <Controller
                                            name={`quantityReceived_${index}`}
                                            control={control}
                                            rules={{
                                                required: "Quantity Received is required",
                                                validate: (value) => {
                                                    const numValue = Number(value);
                                                    const orderedQuantity = Number(item.quantity);
                                                    
                                                    if (isNaN(numValue) || numValue < 0) {
                                                        return "Please enter a valid quantity";
                                                    }
                                                    
                                                    if (numValue > orderedQuantity) {
                                                        return `Quantity received (${numValue}) cannot exceed quantity ordered (${orderedQuantity})`;
                                                    }
                                                    
                                                    return true;
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <Input 
                                                    {...field}
                                                    value={field.value || ""}
                                                    className="w-full"
                                                    placeholder="Enter Quantity Received"
                                                    error={fieldState?.error?.message}
                                                    onChange={field.onChange}
                                                    type="number"
                                                    min="0"
                                                    // max={item.quantity}
                                                    readOnly={false}
                                                />
                                            )}
                                        />
                                    </td>
                                    <td className="p-4 border-b text-center">
                                        <Controller
                                            name={`quantityAccepted_${index}`}
                                            control={control}
                                            rules={{
                                                required: "Quantity Accepted is required",
                                                validate: (value) => {
                                                    const numValue = Number(value);
                                                    const receivedQuantity = Number(watchedValues[`quantityReceived_${index}`]);
                                                    
                                                    if (isNaN(numValue) || numValue < 0) {
                                                        return "Please enter a valid quantity";
                                                    }
                                                    
                                                    if (isNaN(receivedQuantity) || receivedQuantity === 0) {
                                                        return "Please enter quantity received first";
                                                    }
                                                    
                                                    if (numValue > receivedQuantity) {
                                                        return `Quantity accepted (${numValue}) cannot exceed quantity received (${receivedQuantity})`;
                                                    }
                                                    
                                                    return true;
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <Input 
                                                    {...field}
                                                    value={field.value || ""}
                                                    className="w-full"
                                                    placeholder="Enter Quantity Accepted"
                                                    error={fieldState?.error?.message}
                                                    onChange={field.onChange}
                                                    type="number"
                                                    min="0"
                                                    // max={watchedValues[`quantityReceived_${index}`] || 0}
                                                    readOnly={false}
                                                />
                                            )}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    const renderFooterButtons = () => {
        return (
            <Button
                type="submit"
                variant="primary"
                size="lg"
                className="hover:bg-green-700 text-white flex items-center"
                onClick={handleCreateReceipt}
            >
                {loading ? (
                    <RingLoader color="#ffffff" loading={loading} size={20} />
                ) : (
                    <div className="flex gap-3">
                        Submit for Approval <Icon name="noteIcon" />
                    </div>
                )}
            </Button>
        );
    };

    return ( 
        <Modal
            visible={isVisible}
            onCancel={onClose}
            width="80%"
            centered={true}
            footer={null}
            style={{
                padding: 0,
                margin: 0,
                maxWidth: "1200px",
                height: "auto",
            }}
        >
            <h1 className="text-2xl font-bold -mt-2 mb-4">GRN Receipt</h1>
            
            <form onSubmit={handleCreateReceipt}>
                <div className="grid gap-4 py-5">{renderStepContent()}</div>
                {renderFooterButtons()}
            </form>
        </Modal>
    );
};

export default GRNReceipt;